import { useMemo } from "react";
import { clsx } from "clsx";
import { ArrowRightIcon, CheckIcon, PlusIcon } from "@heroicons/react/16/solid";
import { Avatar } from "@/catalyst/avatar.tsx";
import { SidebarItem } from "@/catalyst/sidebar.tsx";
import Link from "@/common/Link";
import { Popover, PopoverContent, PopoverTrigger } from "@/common/Popover";
import { usePopoverContext } from "@/common/Popover/hooks.tsx";
import TwoColumnPicker, {
    PICKER_ROW,
    PICKER_RULE,
} from "@/common/TwoColumnPicker";
import { User } from "@/model/User";
import { Workspace } from "@/types/generated/management-api-types";
import {
    DEMO_WORKSPACES_KEY,
    filterGroups,
    groupByOrganization,
} from "./groups.ts";
import {
    PickerTriggerContent,
    useSwitchWorkspace,
    WorkspaceAvatar,
} from "./parts.tsx";

type PickerProps = {
    selected: Workspace;
    workspaces: Workspace[];
    user: User;
    setNewWorkspaceDialogOpen: SetState<
        { managedOrganizationId?: string } | false
    >;
};

/**
 * The workspace picker of someone who manages other organizations: their
 * clients on the left, the workspaces of the one under the pointer on the
 * right, and a search over both.
 */
export default function ManagerPicker(props: PickerProps) {
    return (
        <Popover
            interactionKind={"hover"}
            autoUpdatePosition={true}
            placement={"bottom-start"}
        >
            <PopoverTrigger asChild={true}>
                <SidebarItem>
                    <PickerTriggerContent selected={props.selected} />
                </SidebarItem>
            </PopoverTrigger>

            <PopoverContent>
                <ManagerPickerPanel {...props} />
            </PopoverContent>
        </Popover>
    );
}

function ManagerPickerPanel({
    selected,
    workspaces,
    user,
    setNewWorkspaceDialogOpen,
}: PickerProps) {
    const { setOpen } = usePopoverContext();
    const switchWorkspace = useSwitchWorkspace();

    const groups = useMemo(() => groupByOrganization(workspaces), [workspaces]);

    return (
        <TwoColumnPicker
            groups={groups}
            itemsOf={(group) => group.workspaces}
            itemKey={(workspace) => workspace.id}
            initialGroupKey={
                selected.isDemo ? DEMO_WORKSPACES_KEY : selected.organization.id
            }
            initialItemKey={selected.id}
            filterGroups={filterGroups}
            onPick={(workspace) => {
                switchWorkspace(workspace);
                setOpen(false);
            }}
            searchLabel={"Search organizations and workspaces"}
            groupsTitle={"Organizations"}
            itemsTitle={"Workspaces"}
            renderGroup={(group) => (
                <>
                    <Avatar
                        className={"size-6"}
                        src={group.favicon}
                        initials={
                            group.name
                                ? group.name.slice(0, 2)
                                : group.key.slice(-2)
                        }
                        generateBgColor={!group.favicon}
                        square
                    />
                    <div className={"flex min-w-0 flex-col"}>
                        <span className={"empty-unknown truncate"}>
                            {group.name}
                        </span>
                        <span className={"truncate text-xs text-subtle"}>
                            {group.detail}
                        </span>
                    </div>
                </>
            )}
            renderItem={(workspace) => (
                <>
                    <WorkspaceAvatar
                        workspace={workspace}
                        className={"size-6 bg-near-white"}
                    />
                    <div className={"flex min-w-0 flex-col"}>
                        <span className={"truncate"}>{workspace.name}</span>
                        <span className={"truncate text-xs text-subtle"}>
                            {workspace.id}
                        </span>
                    </div>
                    {workspace.id === selected.id && (
                        <CheckIcon
                            className={"ml-auto size-4 shrink-0 text-subtle"}
                            aria-label={"Current workspace"}
                        />
                    )}
                </>
            )}
            groupFooter={(group) =>
                group.isOrganization && (
                    <div className={clsx("border-t p-1", PICKER_RULE)}>
                        <button
                            type={"button"}
                            className={clsx(
                                PICKER_ROW,
                                "w-full hover:bg-zinc-950/5 dark:hover:bg-white/10",
                            )}
                            onClick={() => {
                                setNewWorkspaceDialogOpen({
                                    managedOrganizationId:
                                        user.managementOrganization?.id !==
                                        group.key
                                            ? group.key
                                            : undefined,
                                });
                                setOpen(false);
                            }}
                        >
                            <span
                                className={
                                    "flex size-6 shrink-0 items-center justify-center"
                                }
                            >
                                <PlusIcon
                                    className={"size-4 text-subtle"}
                                    aria-hidden={"true"}
                                />
                            </span>
                            New workspace&hellip;
                        </button>
                    </div>
                )
            }
            footer={
                <div
                    className={clsx(
                        "flex justify-end border-t px-4 py-2.5",
                        PICKER_RULE,
                    )}
                    onClick={() => setOpen(false)}
                >
                    <Link
                        to={"/client-manager"}
                        className={
                            "inline-flex items-center gap-1 font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                        }
                    >
                        Client manager
                        <ArrowRightIcon
                            className={"size-4"}
                            aria-hidden={"true"}
                        />
                    </Link>
                </div>
            }
        />
    );
}
